// Which chart the right-hand Chart style panel edits (TODO §15/§7.2).
//
// Smaller than `ganttEditing.tsx`, and for a reason worth stating: SVAR owns its own editor,
// so a gantt has to publish an imperative `api` handle for the panel to drive. We own the
// chart renderer end to end, so the panel edits **the Redux doc** like any other control —
// all that has to cross the tree is *which* doc, as an id.
//
// §7.2 The panel is about the SELECTION rather than about the deck. Selection is claimed on a
// pointer down in the block, exactly as `GanttBlock` does it (§26/§8.1): two charts can sit on
// one slide and both publish as they mount, so "whichever initialised last" would otherwise be
// the one the panel edits for the rest of the session. "The chart I am working in" is the only
// reading a user has of it.
//
// Provided ONLY by `DeckEditor`. The print layer, the presentation overlay and the off-screen
// thumbnail capturer render `<Slide>` outside it and get the inert default, so a read-only
// copy of a slide can never claim the panel — the same trick `GanttEditingProvider` and
// `DiagramEditingProvider` use.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface ChartEditingValue {
  // §5.3 Is this subtree the EDITOR? False in the inert default, which is what the print
  // layer, the presentation overlay and the off-screen thumbnail capturer get — so a chart
  // rendered by one of them can never start a refresh timer. `readOnly` already covers those
  // three, and this covers them a second time from the other direction: an automatic network
  // fetch on an export path is the one failure that would be invisible until a deck was
  // presented offline (§5.3.4), so it is worth two locks rather than one.
  isEditor: boolean;
  // The chartDocId the panel is editing. Null when no chart is mounted in an editable window.
  activeDocId: string | null;
  // Claim the panel — called by `ChartBlock` on mount and on every pointer down inside it.
  publish: (docId: string) => void;
  // Give it up on unmount. Takes the docId rather than clearing unconditionally: two charts on
  // a two-window slide mount and unmount in an order React chooses, and a bare clear would let
  // the first one's unmount wipe the second one's claim (the bug `GanttEditingProvider`
  // documents, in a component that has no api object to compare instead).
  release: (docId: string) => void;
}

const INERT: ChartEditingValue = {
  isEditor: false,
  activeDocId: null,
  publish: () => {},
  release: () => {},
};

const ChartEditingContext = createContext<ChartEditingValue>(INERT);

export function ChartEditingProvider({ children }: { children: ReactNode }) {
  const [activeDocId, setActiveDocId] = useState<string | null>(null);

  const publish = useCallback((docId: string) => {
    setActiveDocId((prev) => (prev === docId ? prev : docId));
  }, []);

  const release = useCallback((docId: string) => {
    setActiveDocId((prev) => (prev === docId ? null : prev));
  }, []);

  const value = useMemo<ChartEditingValue>(
    () => ({ isEditor: true, activeDocId, publish, release }),
    [activeDocId, publish, release],
  );

  return (
    <ChartEditingContext.Provider value={value}>
      {children}
    </ChartEditingContext.Provider>
  );
}

export function useChartEditing(): ChartEditingValue {
  return useContext(ChartEditingContext);
}
